import { Component, h, InjectionKey } from 'vue';

import { BASE_MENU_VALUE } from '@/common/components/organisms/baseMenu/baseMenu.define';
import type { MenuInfo } from '@/common/components/organisms/baseMenu/types';

import {
  DashboardPermissionCode,
  MenuSearchContentComponent,
  MenuSearchSharedData,
  MenuSearchStateData,
} from './menuSearch.type';
import MenuSearchDefault from './menuSearchContent/menuSearchDefault/MenuSearchDefault.vue';
import MenuSearchEmpty from './menuSearchContent/MenuSearchEmpty.vue';
import MenuSearchNoResult from './menuSearchContent/MenuSearchNoResult.vue';
import MenuSearchResult from './menuSearchContent/menuSearchResult/MenuSearchResult.vue';
import MenuSearchError from './menuSearchContent/MenuSearchError.vue';
import RaindropSpinner from '@/common/components/molecules/loading/raindropSpinner/RaindropSpinner.vue';

// 높이 계산 상수
export const CONTENT_MAX_HEIGHT = 560; // menuSearchContent의 max-height
export const WINDOW_MAX_HEIGHT = 652; // content가 max-height일 때 window의 전체 높이
const WINDOW_PADDING = 16; // window padding (8px * 2)
const SEARCH_HEIGHT = 36; // search 영역 높이
const SEARCH_CONTENT_GAP = 6; // search와 content 사이 gap
const CONTENT_PADDING = 16; // content 영역 padding (8px * 2)
const FOOTER_HEIGHT = 42; // footer 높이 (34px + 8px padding-top)
const CONTENT_FOOTER_GAP = 12; // content와 footer 사이 gap
export const TOTAL_EXTRA_HEIGHT =
  WINDOW_PADDING +
  SEARCH_HEIGHT +
  SEARCH_CONTENT_GAP +
  CONTENT_PADDING +
  CONTENT_FOOTER_GAP +
  FOOTER_HEIGHT; // 138px

// 필터링 제외 대상 (완전히 제외)
export const EXCLUDED_MENU_VALUES: string[] = [BASE_MENU_VALUE.HELP, BASE_MENU_VALUE.SETTINGS];

// 하위 메뉴 없이 1-Depth 클릭만으로 이동하는 루트 메뉴와 그 기본 경로
// 자식이 없어 일반 재귀 탐색으로는 검색 결과에 남지 않으므로 별도 처리 대상이다.
export const ROOT_LEAF_MENU_PATH: Record<string, string> = {
  [BASE_MENU_VALUE.HOME]: '/home',
  [BASE_MENU_VALUE.DASHBOARD]: '/dashboard/list',
  [BASE_MENU_VALUE.AIOPS]: '/aiops/dashboard',
};

export const DASHBOARD_TYPE = {
  PRESET: 'WORD.PRESET',
  CUSTOM: 'WORD.CUSTOM_WORD',
};
// 배열 타입 필드명
export const ARRAY_FIELDS: Array<keyof Pick<MenuInfo, 'children' | 'subMenuList'>> = [
  'children',
  'subMenuList',
];

// 객체 타입 필드명
export const OBJECT_FIELDS: Array<keyof Pick<MenuInfo, 'dashboardList' | 'analysisBoardList'>> = [
  'dashboardList',
  'analysisBoardList',
];

export const MENU_SEARCH_CONTENT_COMPONENT: Record<MenuSearchContentComponent, Component> = {
  EMPTY: MenuSearchEmpty,
  NO_RESULT: MenuSearchNoResult,
  DEFAULT: MenuSearchDefault,
  RESULT: MenuSearchResult,
  LOADING: () => h('div', { class: 'menu-search-loading-spinner' }, [h(RaindropSpinner)]),
  ERROR: MenuSearchError,
} as const;

// 대시보드 권한 코드 상수
export const DASHBOARD_PERMISSION_CODE: Record<DashboardPermissionCode, string> = {
  DASHBOARD_ACCESS_PASS: '',
  DASHBOARD_DELETED: 'MESSAGE.DASHBOARD.DELETED_DASHBOARD',
  DASHBOARD_NOT_PERMISSION: 'MESSAGE.NO_PERMISSION',
} as const;

export const DASHBOARD_PERMISSION_CODE_KEYS: string[] = Object.keys(DASHBOARD_PERMISSION_CODE);

export const DASHBOARD_NOT_PERMISSION_CODE_LIST: string[] = DASHBOARD_PERMISSION_CODE_KEYS.filter(
  (code) => code === 'DASHBOARD_DELETED' || code === 'DASHBOARD_NOT_PERMISSION',
);

export const dashboardListKey = ['v8-dashboards-dashboard', { includeImage: false }] as const;

export const MENU_SEARCH_SHARED_DATA_KEY: InjectionKey<MenuSearchSharedData> =
  Symbol('MenuSearchSharedData');
export const MENU_SEARCH_STATE_KEY: InjectionKey<MenuSearchStateData> = Symbol('MenuSearchState');
